import { FC, PropsWithChildren } from 'react';

import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';

import { useActivitySwings, useSwingAnalysis, useSwingImages } from '@queries';
import {
    activeAnalysisAtom,
    activeUserSettingsAtom,
    currentActivityIdAtom,
    userSettingsAtom,
    useSyncAtom,
} from '@store';

import { swingsAtom } from '../store/activity';
import { activeSwingIdAtom, swingImagesAtom } from '../store/swing';

const ActivityScope: FC<PropsWithChildren> = ({ children }) => {
    const activityId = useAtomValue(currentActivityIdAtom);
    return (
        <ScopeProvider key={activityId} atoms={[swingsAtom, swingImagesAtom, activeAnalysisAtom, activeSwingIdAtom]}>
            {children}
        </ScopeProvider>
    );
};

const ActivityLoader: FC<PropsWithChildren> = ({ children }) => {
    const activityId = useAtomValue(currentActivityIdAtom);
    const userSettings = useAtomValue(userSettingsAtom);
    useSyncAtom(activeUserSettingsAtom, userSettings);

    const { data: swings } = useActivitySwings(activityId);
    useSyncAtom(swingsAtom, swings);

    return children;
};

export const SwingLoader: FC<PropsWithChildren> = ({ children }) => {
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const { data: images } = useSwingImages(activeSwingId ?? '');
    useSyncAtom(swingImagesAtom, images);
    const { data: swingAnalysis } = useSwingAnalysis(activeSwingId);
    useSyncAtom(activeAnalysisAtom, swingAnalysis);
    return children;
};

export const ActivityGuard: FC<PropsWithChildren> = ({ children }) => {
    return (
        <ActivityScope>
            <ActivityLoader>
                <SwingLoader>{children}</SwingLoader>
            </ActivityLoader>
        </ActivityScope>
    );
};
